import React from 'react';
import { useSelector } from 'react-redux';

import { PERMISSION_GRANTED } from 'constants/notifications';
import {
  REQUEST_PUSH_PERMISSIONS,
  SHOW_MEMBER_VIDEO_THRESHOLD_BANNER,
  SHOW_DATA_RETENTION_WARNING,
  SHOW_MOBILE_BANNER_PROMPT_ON_SHARE_PAGE,
} from 'constants/triggers';

import { useShouldShowTrigger } from 'hooks/triggers';
import { useGetMemberVideoLimits } from 'hooks/workspace';
import { selectUserIsOwner } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import MemberVideoThresholdBanner from './MemberVideoThresholdBanner';
import { ROLLOUT_WEB_PERMISSIONS_REQUEST_BANNERS } from '../../../../../shared/constants/featureFlag';

import MobileDownloadBanner from 'components/account-settings/mobile-download-banner';
import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import { SharePageDataRetentionBanner } from 'components/share-video/data-retention-banner';
import WebPermissionsBanner from 'components/web-permissions-banners/share-page';

const Banners = () => {
  const isLoggedIn = useSelector(selectUserIsLogged);
  const shouldRequestPushPermissions = useShouldShowTrigger(
    REQUEST_PUSH_PERMISSIONS
  );
  const hasRecordedFirstVideo = useSelector(
    state => state.checklist.first_video_recording
  );

  const isVideoOwner = useSelector(selectUserIsOwner);

  const shouldShowMobileBanner = useShouldShowTrigger(
    SHOW_MOBILE_BANNER_PROMPT_ON_SHARE_PAGE
  );

  const shouldShowDataRetentionBanner =
    useShouldShowTrigger(SHOW_DATA_RETENTION_WARNING) && isVideoOwner;

  const browserSupport = 'Notification' in window;

  const memberVideoLimit = useGetMemberVideoLimits();
  const showMemberVideoThresholdBanner = useShouldShowTrigger(
    SHOW_MEMBER_VIDEO_THRESHOLD_BANNER
  );

  if (showMemberVideoThresholdBanner && memberVideoLimit.nearLimit) {
    return <MemberVideoThresholdBanner memberVideoLimit={memberVideoLimit} />;
  }

  const showWebPermissions =
    isLoggedIn &&
    isVideoOwner &&
    shouldRequestPushPermissions &&
    hasRecordedFirstVideo &&
    browserSupport &&
    Notification.permission !== PERMISSION_GRANTED;

  if (showWebPermissions) {
    return (
      <FeatureFlaggedComponent
        flag={ROLLOUT_WEB_PERMISSIONS_REQUEST_BANNERS}
        activationValues={['variant']}
      >
        <WebPermissionsBanner />
      </FeatureFlaggedComponent>
    );
  }

  const showMobileBanner =
    isLoggedIn &&
    shouldShowMobileBanner &&
    hasRecordedFirstVideo &&
    browserSupport &&
    Notification.permission === PERMISSION_GRANTED;

  if (showMobileBanner) {
    return <MobileDownloadBanner onSharePage />;
  }

  if (shouldShowDataRetentionBanner) {
    return <SharePageDataRetentionBanner />;
  }

  return null;
};

export default Banners;
